import {
	Group as GroupIcon,
	PlayArrow as PlayIcon,
	Security as SecurityIcon,
	Speed as SpeedIcon,
} from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Container,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { useRetro } from "../context/RetroContext";

export function LandingPage() {
	const { state, createSession, joinSession } = useRetro();
	const [sessionId, setSessionId] = useState("");
	const [userName, setUserName] = useState(state.userName || "");
	const [error, setError] = useState("");
	const [hasSessionFromUrl, setHasSessionFromUrl] = useState(false);
	const [hasAttemptedAutoJoin, setHasAttemptedAutoJoin] = useState(false);

	// Check for session ID in URL params
	useEffect(() => {
		const checkUrlParams = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const sessionParam = urlParams.get("session");
			if (sessionParam) {
				setSessionId(sessionParam);
				setHasSessionFromUrl(true);
				setHasAttemptedAutoJoin(false); // Reset auto-join flag for new session
			} else {
				// URL was cleared (e.g., session was closed)
				setSessionId("");
				setHasSessionFromUrl(false);
				setHasAttemptedAutoJoin(false);
			}
		};

		checkUrlParams();

		// Listen for URL changes (e.g., when session is closed)
		const handlePopState = () => {
			checkUrlParams();
		};

		window.addEventListener("popstate", handlePopState);

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, []);

	// Auto-attempt to join session if URL has session ID and we have a username (only once)
	useEffect(() => {
		const autoJoinSession = async () => {
			if (
				hasSessionFromUrl &&
				sessionId &&
				userName &&
				userName.trim().length >= 2 &&
				!state.loading &&
				!state.session &&
				!hasAttemptedAutoJoin &&
				!state.sessionClosed
			) {
				setHasAttemptedAutoJoin(true);
				try {
					setError("");
					await joinSession(sessionId.trim(), userName.trim());
				} catch (_err) {
					setError(
						"This session has been cancelled or no longer exists. You can create a new session below.",
					);
					// Clear the session ID from URL and state
					setSessionId("");
					setHasSessionFromUrl(false);
					// Update URL to remove session parameter
					const url = new URL(window.location.href);
					url.searchParams.delete("session");
					window.history.replaceState({}, "", url.toString());
				}
			}
		};

		// Add a small delay to prevent auto-join while user is actively typing
		const timeoutId = setTimeout(() => {
			autoJoinSession();
		}, 1000);

		return () => clearTimeout(timeoutId);
	}, [
		hasSessionFromUrl,
		sessionId,
		userName,
		state.loading,
		state.session,
		hasAttemptedAutoJoin,
		state.sessionClosed,
		joinSession,
	]);

	// Update username when state changes (e.g., after session closure)
	useEffect(() => {
		if (state.userName && state.userName !== userName) {
			setUserName(state.userName);
		}
	}, [state.userName, userName]);

	// Reset auto-join flag when session is closed
	useEffect(() => {
		if (state.sessionClosed) {
			setHasAttemptedAutoJoin(false);
		}
	}, [state.sessionClosed]);

	const handleCreateSession = async () => {
		if (!userName.trim()) {
			setError("Please enter your name");
			return;
		}

		try {
			setError("");
			await createSession(userName.trim());
		} catch (_err) {
			setError("Failed to create session. Please try again.");
		}
	};

	const handleJoinSession = async () => {
		if (!sessionId.trim()) {
			setError("Please enter a session ID");
			return;
		}

		if (!userName.trim()) {
			setError("Please enter your name");
			return;
		}

		try {
			setError("");
			await joinSession(sessionId.trim(), userName.trim());
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: "Failed to join session. The session may have been cancelled or the session ID is invalid.";
			setError(errorMessage);
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			handleJoinSession();
		}
	};

	if (state.loading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="100vh"
				flexDirection="column"
				gap={2}
			>
				<CircularProgress size={60} />
				<Typography variant="h6" color="text.secondary">
					{state.session ? "Loading session..." : "Creating session..."}
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
				display: "flex",
				alignItems: "center",
				py: 4,
			}}
		>
			<Container maxWidth="lg">
				<Box
					display="flex"
					flexDirection={{ xs: "column", md: "row" }}
					gap={4}
					alignItems="center"
				>
					<Box flex={1}>
						<Box sx={{ color: "white", mb: 4 }}>
							<Typography
								variant="h2"
								component="h1"
								gutterBottom
								sx={{
									fontWeight: "bold",
									textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
								}}
							>
								Retrospective Tool
							</Typography>
							<Typography
								variant="h5"
								component="p"
								sx={{
									opacity: 0.9,
									textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
								}}
							>
								Run effective sprint retrospectives with your team. No signup
								required - just create a session and share the link!
							</Typography>
						</Box>

						<Box
							display="flex"
							flexDirection={{ xs: "column", sm: "row" }}
							gap={2}
						>
							<Box flex={1}>
								<Card sx={{ height: "100%", textAlign: "center" }}>
									<CardContent>
										<GroupIcon
											sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
										/>
										<Typography variant="h6" gutterBottom>
											Team Collaboration
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Real-time updates for all participants
										</Typography>
									</CardContent>
								</Card>
							</Box>

							<Box flex={1}>
								<Card sx={{ height: "100%", textAlign: "center" }}>
									<CardContent>
										<SpeedIcon
											sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
										/>
										<Typography variant="h6" gutterBottom>
											Quick & Easy
										</Typography>
										<Typography variant="body2" color="text.secondary">
											No setup required, just start retrospecting
										</Typography>
									</CardContent>
								</Card>
							</Box>
						</Box>
					</Box>

					<Box flex={1}>
						<Paper sx={{ p: 4, borderRadius: 2 }}>
							<Typography variant="h4" gutterBottom align="center">
								Get Started
							</Typography>

							<Typography
								variant="body1"
								color="text.secondary"
								align="center"
								sx={{ mb: 4 }}
							>
								{hasSessionFromUrl
									? "Enter your name to join the retrospective session"
									: "Create a new retrospective session or join an existing one"}
							</Typography>

							{error && (
								<Alert severity="error" sx={{ mb: 3 }}>
									{error}
								</Alert>
							)}

							<Box sx={{ mb: 3 }}>
								<TextField
									fullWidth
									label="Your Name"
									value={userName}
									onChange={(e) => setUserName(e.target.value)}
									placeholder="Enter your name"
									variant="outlined"
									sx={{ mb: 3 }}
									required
									error={!userName.trim() && hasSessionFromUrl}
									helperText={
										hasSessionFromUrl && !userName.trim()
											? "Name is required to join the session"
											: ""
									}
								/>
							</Box>

							<Box sx={{ mb: 4 }}>
								{!hasSessionFromUrl && (
									<>
										<Button
											variant="contained"
											size="large"
											fullWidth
											startIcon={<PlayIcon />}
											onClick={handleCreateSession}
											disabled={state.loading || !userName.trim()}
											sx={{ mb: 2, py: 1.5 }}
										>
											Create New Session
										</Button>

										<Typography
											variant="body2"
											color="text.secondary"
											align="center"
											sx={{ mb: 3 }}
										>
											OR
										</Typography>

										<Box sx={{ display: "flex", gap: 1 }}>
											<TextField
												fullWidth
												label="Session ID"
												value={sessionId}
												onChange={(e) => setSessionId(e.target.value)}
												onKeyPress={handleKeyPress}
												placeholder="Enter session ID to join"
												variant="outlined"
											/>
											<Button
												variant="outlined"
												size="large"
												onClick={handleJoinSession}
												disabled={
													state.loading || !sessionId.trim() || !userName.trim()
												}
												sx={{ px: 3 }}
											>
												Join
											</Button>
										</Box>
									</>
								)}

								{hasSessionFromUrl && (
									<Button
										variant="contained"
										size="large"
										fullWidth
										startIcon={<PlayIcon />}
										onClick={handleJoinSession}
										disabled={state.loading || !userName.trim()}
										sx={{ py: 1.5 }}
									>
										Join Session
									</Button>
								)}
							</Box>

							<Box sx={{ textAlign: "center" }}>
								<SecurityIcon
									sx={{ fontSize: 20, color: "text.secondary", mr: 1 }}
								/>
								<Typography variant="caption" color="text.secondary">
									No data is stored permanently. Sessions are deleted when
									closed.
								</Typography>
							</Box>
						</Paper>
					</Box>
				</Box>
			</Container>
		</Box>
	);
}
